import React, {PureComponent, ReactNode} from 'react'
import MeetingComment, {CommentAttachment} from '../model/MeetingComment'
import {getLinkIconComponent, IconBox, MiddleFontSpan, PreWrap, TrashIcon} from '../AssocDirectorCommon'
import MeetingCommentDetail from '../model/MeetingCommentDetail'
import { Box, Drawer, Popover } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import MeetingCommentView from '../model/MeetingCommentView'
import {
    CenterRowFlexContainer,
    FlexContainer,
    FullWidthLeftRowFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
    RowFlexContainer,
} from '../../../styledComponents/FlexComponents'
import DeletedComment from '../model/DeletedComment'
import DeletedCommentDetail from '../model/DeletedCommentDetail'
import DeletedCommentView from '../model/DeletedCommentView'
import {Nullable} from '../../../app/TypeHelpers'
import styled from 'styled-components'
import {StyledHR} from '../../../styledComponents/StyledTypeDefinitions'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faDownload, faEllipsisH, faPen, faTrash} from '@fortawesome/free-solid-svg-icons'
import MeetingCommentReply from '../model/MeetingCommentReply'
import DeletedCommentReply from '../model/DeletedCommentReply'
import {UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import {getFileExtension} from '../../../__NoAutomatedTests/common/util/Natutil'
import {currentMedia, HideInPC, HideInSP} from '../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDivider} from '../../../styledComponents/DisplayItem'
import {MeetingCommentFile} from '../model/MeetingCommentFile'
import {ErrorMessage, ErrorMessageArea, ErrorMessageTitle} from './MeetingCommentInputForm'
import {MeetingHelper} from '../utils/MeetingHelper'
import MessageDialogComponent from '../../../common/resident/MessageDialogComponent'
import {Messages} from '../../../Messages'
import {NoWrapSpan} from '../shared/AssocComponents'

interface Props {
    condominiumCd: string
    meetingId: number
    meetingComment: MeetingComment | MeetingCommentDetail | MeetingCommentView | MeetingCommentReply |
        DeletedComment | DeletedCommentDetail | DeletedCommentView | DeletedCommentReply
    onClickReaction: (commentId: number, isReaction: boolean) => void
    onClickCommentDelete: (commentId: number) => void
    onClickEditButton: (commentId: number,
                        version: number,
                        content: string,
                        commentFileList: MeetingCommentFile[]) => void
    errorFileNameList?: string[]
    havePostCommentAuthority: boolean
    verticalDivider?: boolean
    currentDatetime: string
}

interface State {
    isOpenReactionArea: boolean
    isClosingReactionArea: boolean
    isOpenCommentMenu: boolean
    isOpenCommentDeleteDialog: boolean
    reactionButton: Nullable<HTMLElement>
    commentMenuButton: Nullable<HTMLElement>
}

class MeetingCommentComponent extends PureComponent<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            isOpenReactionArea: false,
            isClosingReactionArea: false,
            isOpenCommentMenu: false,
            isOpenCommentDeleteDialog: false,
            reactionButton: null,
            commentMenuButton: null,
        }
    }

    handleClickReactionOpenButton = (event: React.MouseEvent<HTMLElement>): void => {
        this.setState({
            isOpenReactionArea: true,
            reactionButton: event.currentTarget,
        })
    }

    handleCloseReactionArea = (): void => {
        this.setState({
            isOpenReactionArea: false,
            reactionButton: null,
        })
    }

    handleExitedReactionArea = (): void => {
        this.setState({isClosingReactionArea: false})
    }

    handleClickReactionButtonForDrawer = (isReaction: boolean) => (): void => {
        if (this.state.isClosingReactionArea) {
            return
        }

        const {onClickReaction, meetingComment} = this.props
        onClickReaction(meetingComment.meetingCommentId, isReaction)
        this.setState({
            isClosingReactionArea: true,
        }, () => {
            setTimeout(() => {
                this.setState({
                    isOpenReactionArea: false,
                })
            }, 500)
        })
    }

    handleClickReactionButtonForPopover = (isReaction: boolean) => (): void => {
        if (this.state.isClosingReactionArea) {
            return
        }

        const {onClickReaction, meetingComment} = this.props
        onClickReaction(meetingComment.meetingCommentId, isReaction)
        this.setState({
            isClosingReactionArea: true,
        }, () => {
            setTimeout(() => {
                this.setState({
                    isOpenReactionArea: false,
                    isClosingReactionArea: false,
                })
            }, 500)
        })
    }

    handleClickCommentMenuButton = (event: React.MouseEvent<HTMLElement>): void => {
        this.setState({
            isOpenCommentMenu: true,
            commentMenuButton: event.currentTarget,
        })
    }

    handleCloseCommentMenu = (): void => {
        this.setState({
            isOpenCommentMenu: false,
            commentMenuButton: null,
        })
    }

    handleClickCommentDeleteButton = (): void => {
        this.setState({
            isOpenCommentMenu: false,
            isOpenCommentDeleteDialog: true,
        })
    }

    handleClickDeleteCancelButton = (): void => {
        this.setState({
            isOpenCommentDeleteDialog: false,
        })
    }

    handleClickDeleteOkButton = (): void => {
        const {onClickCommentDelete, meetingComment} = this.props
        onClickCommentDelete(meetingComment.meetingCommentId)
        this.setState({
            isOpenCommentDeleteDialog: false,
        })
    }

    handleClickCommentEditButton = (): void => {
        const {meetingComment, onClickEditButton} = this.props
        if (meetingComment instanceof MeetingComment) {
            const commentFileList: MeetingCommentFile[] = []
            meetingComment.attachmentList.forEach(attachment =>
                commentFileList.push({
                    fileNumber: attachment.id,
                    fileData: attachment.uri,
                    fileName: attachment.displayName,
                    fileSize: 0,
                }),
            )

            onClickEditButton(
                meetingComment.meetingCommentId,
                meetingComment.version,
                meetingComment.content,
                commentFileList,
            )
        }
        this.setState({
            isOpenCommentMenu: false,
        })
    }

    renderAttachment(attachmentList: CommentAttachment[]): ReactNode {
        return attachmentList.map((attachment, index) => {
            return <AttachmentFile key={`attachment-${index}`}>
                <AttachmentFileLink to={attachment.uri}>
                    <FullWidthLeftRowFlexContainer>
                        <AttachmentFileIconBox>
                            {getLinkIconComponent(getFileExtension(attachment.displayName))}
                        </AttachmentFileIconBox>
                        <AttachmentFileName>{attachment.displayName}</AttachmentFileName>
                        <AttachmentFileIconBox>
                            <FontAwesomeIcon icon={faDownload}/>
                        </AttachmentFileIconBox>
                    </FullWidthLeftRowFlexContainer>
                </AttachmentFileLink>
            </AttachmentFile>
        })
    }

    renderDeleteComment(deleteComment: DeletedComment | DeletedCommentDetail | DeletedCommentView): ReactNode {
        return (<>
            <LeftRowFlexContainer>
                <TrashIcon/>
                <Box ml={'5px'}>
                    <MiddleFontSpan>{deleteComment.message}</MiddleFontSpan>
                </Box>
            </LeftRowFlexContainer>
        </>)
    }

    renderCommentHeader(comment: MeetingComment | MeetingCommentDetail | MeetingCommentView): ReactNode {
        const commentMenuNode = <>
            <StyledButton onClick={this.handleClickCommentEditButton}>
                <LeftRowFlexContainer>
                    <FontAwesomeIcon icon={faPen}/>
                    <Box width={'15px'}/>
                    コメントを編集
                </LeftRowFlexContainer>
            </StyledButton>
            <DisplayItemDivider/>
            <StyledButton onClick={this.handleClickCommentDeleteButton}>
                <LeftRowFlexContainer>
                    <FontAwesomeIcon icon={faTrash}/>
                    <Box width={'15px'}/>
                    コメントを削除
                </LeftRowFlexContainer>
            </StyledButton>
        </>

        return <RowFlexContainer>
            <LeftTopRowFlexContainer>
                <Box mt={'4px'}>
                    {MeetingHelper.getUserIconNodeByResidentStatus(
                        comment.user.isWithinTerm,
                        comment.user.hasMailAddress,
                        comment.user.role,
                        comment.user.roomList,
                        'large',
                    )}
                </Box>
                <Box ml={'8px'}>
                    <UserNameAndRoomDisplayArea>
                        <NoWrapSpan>
                            <UserNameDiv>
                                {MeetingHelper.createCommentUserNameByResidentStatus(comment.user)}
                            </UserNameDiv>
                        </NoWrapSpan>
                        <Box width={'4px'}/>
                        <NoWrapSpan>
                            <RoomNumberText>
                                {MeetingHelper.getDisplayRoomListByResidentStatus(comment.user.roomList)}
                            </RoomNumberText>
                        </NoWrapSpan>
                    </UserNameAndRoomDisplayArea>
                    <CommentDatetime>
                        {MeetingHelper.formatDoNotDisplayCurrentYear(
                            this.props.currentDatetime, comment.updateDatetime,
                        )} {comment.version > 1 && '編集済み'}
                    </CommentDatetime>
                </Box>
            </LeftTopRowFlexContainer>
            {
                this.props.havePostCommentAuthority && comment.isYou &&
                <div>
                    <UnstyledLinkStyledButton
                        id={'commentMenuButton'}
                        onClick={this.handleClickCommentMenuButton}
                    >
                        <FontAwesomeIcon icon={faEllipsisH} color={Colors.AccentDark}/>
                    </UnstyledLinkStyledButton>
                    <HideInSP>
                        <CommentMenuPopover
                            open={this.state.isOpenCommentMenu}
                            onClose={this.handleCloseCommentMenu}
                            anchorEl={this.state.commentMenuButton}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                            transformOrigin={{vertical: 'top', horizontal: 'center'}}
                        >
                            {commentMenuNode}
                        </CommentMenuPopover>
                    </HideInSP>
                    <HideInPC>
                        <CommentMenuDrawer
                            anchor={'bottom'}
                            open={this.state.isOpenCommentMenu}
                            onClose={this.handleCloseCommentMenu}
                        >
                            {commentMenuNode}
                        </CommentMenuDrawer>
                    </HideInPC>
                </div>
            }
        </RowFlexContainer>
    }

    renderCommentBody(comment: MeetingComment | MeetingCommentDetail | MeetingCommentView): ReactNode {
        const commentReactedUserList = comment.commentReactedUserList
        const isYouReaction = commentReactedUserList.some(commentReactedUser => commentReactedUser.isYou)
        const reactionCount = comment.commentReactedUserList.length

        const reactionListNode = <>
            <RowFlexContainer>
                <ReactionTitle>見ましたを押した人({reactionCount})</ReactionTitle>
                <UnstyledLinkStyledButton
                    onClick={this.handleClickReactionButtonForPopover(!isYouReaction)}
                >
                    <img
                        src={isYouReaction ? '/resources/images/icons/icon_mimashita.svg' : '/resources/images/icons/icon_mimashita_dotted.svg'}
                        width={'40px'}
                        alt={'見ました'}
                    />
                </UnstyledLinkStyledButton>
            </RowFlexContainer>
            <Box height={'12px'}/>
            <ReactionListContainer>
                {
                    commentReactedUserList.map((user, index) => {
                        let displayName = MeetingHelper.createCommentUserNameByResidentStatus({
                            firstName: user.firstName,
                            familyName: user.familyName,
                            role: user.role,
                            roomList: user.roomList,
                            isWithinTerm: user.isWithinTerm,
                            hasMailAddress: user.hasMailAddress,
                        })
                        if (user.isYou) {
                            displayName += ' (あなた)'
                        }
                        return <Box key={'commentReactedUser-' + index} mt={'2px'} minHeight={'28px'}>
                            <LeftRowFlexContainer>
                                <CommentReactedUserIconBox>
                                    {MeetingHelper.getUserIconNodeByResidentStatus(
                                        user.isWithinTerm,
                                        user.hasMailAddress,
                                        user.role,
                                        user.roomList,
                                        'medium',
                                    )}
                                </CommentReactedUserIconBox>
                                <UserNameAndRoomDisplayArea>
                                    <NoWrapSpan>
                                        <MiddleFontSpan>{displayName}</MiddleFontSpan>
                                    </NoWrapSpan>
                                    <Box width={'4px'}/>
                                    <NoWrapSpan>
                                        <RoomNumberText>
                                            {MeetingHelper.getDisplayRoomListByResidentStatus(user.roomList)}
                                        </RoomNumberText>
                                    </NoWrapSpan>
                                </UserNameAndRoomDisplayArea>
                            </LeftRowFlexContainer>
                        </Box>
                    })
                }
            </ReactionListContainer>
        </>

        const {errorFileNameList} = this.props
        return <Box mt={'5px'} mb={'15px'}>
            {
                errorFileNameList && errorFileNameList.length > 0 &&
                <ErrorMessageArea>
                    <ErrorMessageTitle>
                        資料のアップロードに失敗しました。
                    </ErrorMessageTitle>
                    <Box height={'5px'}/>
                    <ErrorMessage>
                        アップロードの最中に問題が発生しました。<br/>
                        編集から再度アップロードしてください。
                    </ErrorMessage>
                    <Box height={'10px'}/>
                    {
                        errorFileNameList.map((fileName, index) => {
                            return <ErrorMessage key={'errorFileName-' + index}>
                                {fileName}
                            </ErrorMessage>
                        })
                    }
                </ErrorMessageArea>
            }
            <PreWrap>{comment.content}</PreWrap>
            {this.renderAttachment(comment.attachmentList)}
            <Box height={'5px'}/>
            <RowReverseFlexContainer>
                <UnstyledLinkStyledButton
                    onClick={this.handleClickReactionOpenButton}
                >
                    <LeftRowFlexContainer>
                        <IconBox>
                            <img
                                src={isYouReaction ? '/resources/images/icons/icon_mimashita.svg' : '/resources/images/icons/icon_mimashita_grey.svg'}
                                width={'25px'}
                                alt={'見ました'}
                            />
                        </IconBox>
                        <ReactionLabel>
                            <RowFlexContainer>
                                <Box minWidth={'3px'}>{reactionCount > 0 ? reactionCount : ''}</Box>
                            </RowFlexContainer>
                        </ReactionLabel>
                    </LeftRowFlexContainer>
                </UnstyledLinkStyledButton>
            </RowReverseFlexContainer>
            <HideInSP>
                <ReactionPopover
                    open={this.state.isOpenReactionArea}
                    onClose={this.handleCloseReactionArea}
                    anchorEl={this.state.reactionButton}
                    anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                >
                    <Box p={'10px 10px 10px 15px'}>
                        {reactionListNode}
                    </Box>
                </ReactionPopover>
            </HideInSP>
            <HideInPC>
                <ReactionDrawer
                    anchor={'bottom'}
                    open={this.state.isOpenReactionArea}
                    onClose={this.handleCloseReactionArea}
                    SlideProps={{onExited: this.handleExitedReactionArea}}
                >
                    <Box m={'15px 15px 0px 15px'}>
                        {reactionListNode}
                    </Box>
                </ReactionDrawer>
            </HideInPC>
        </Box>
    }

    render(): ReactNode {
        const {meetingComment, verticalDivider} = this.props
        if (meetingComment instanceof DeletedComment) {
            const isDeletedCommentReply = meetingComment instanceof DeletedCommentReply
            return (<>
                <Box>{this.renderDeleteComment(meetingComment)}</Box>
                {
                    isDeletedCommentReply &&
                    <StretchRowFlexContainer>
                        <VerticalDividerContainer>
                            {
                                verticalDivider &&
                                <VerticalDivider/>
                            }
                        </VerticalDividerContainer>
                        <ContentBodyBox><Box height={'20px'}/></ContentBodyBox>
                    </StretchRowFlexContainer>
                }
            </>)
        }

        const isCommentReply = meetingComment instanceof MeetingCommentReply
        const {
            isOpenCommentDeleteDialog,
        } = this.state
        return (<>
            {this.renderCommentHeader(meetingComment)}
            {
                isCommentReply ?
                    <StretchRowFlexContainer>
                        <VerticalDividerContainer>
                            {
                                verticalDivider &&
                                <VerticalDivider/>
                            }
                        </VerticalDividerContainer>
                        <ContentBodyBox>{this.renderCommentBody(meetingComment)}</ContentBodyBox>
                    </StretchRowFlexContainer>
                    :
                    <Box>{this.renderCommentBody(meetingComment)}</Box>
            }
            {
                isOpenCommentDeleteDialog &&
                <MessageDialogComponent
                    open={isOpenCommentDeleteDialog}
                    params={{
                        title: 'コメントを削除',
                        errorLevel: Messages.WASOR0001.errorLevel,
                        message: Messages.WASOR0001.toString(),
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'キャンセル',
                                buttonHandler: this.handleClickDeleteCancelButton,
                            },
                            {
                                isDefault: false,
                                buttonLabel: '削除',
                                buttonHandler: this.handleClickDeleteOkButton,
                            },
                        ],
                    }}
                />
            }
        </>)
    }
}

export default MeetingCommentComponent

const StretchRowFlexContainer = styled(LeftRowFlexContainer)`
    align-items: stretch;
`

const VerticalDividerContainer = styled(CenterRowFlexContainer)`
    align-items: stretch;
    min-width: 32px;
`

const VerticalDivider: StyledHR = styled.hr`
    margin: 0;
    width: 3px;
    height: 100%;
    border: none;
    background-color: ${Colors.Background};
`

const ContentBodyBox = styled(Box)`
    flex-grow: 2;
    overflow: hidden;
`

const UserNameDiv = styled.div`
    font-weight: ${FontWeights.Bold};
    font-size: ${FontSize.Middle};
`

const CommentDatetime = styled.div`
    font-size: ${FontSize.Small};
    color: ${Colors.AccentDark};
`

const AttachmentFileIconBox = styled(IconBox)`
    color: ${Colors.AccentDark};
`

export const AttachmentFileName = styled.label`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
`

const ReactionLabel = styled.label`
    font-size: ${FontSize.Middle};
    color: ${Colors.AccentDark};
`

const AttachmentFile = styled.div`
    margin-top: 5px;
    padding: 5px 10px;
    border: 1px solid ${Colors.Background};
    border-radius: 4px;
`

const AttachmentFileLink = withStyles({
    root: {
        color: Colors.Body,
        fontSize: FontSize.Body,
        lineHeight: 1.5,
    },
})(StyledLink)

const ReactionListContainer = styled.div`
    ${currentMedia.lessThan('sp')`
    height: 130px;
  `}
    ${currentMedia.greaterThan('pc')`
    max-height: 300px;
  `}

    overflow: auto;
`
const ReactionPopover = withStyles({
    paper: {
        minWidth: '400px',
    },
})(Popover)

const ReactionDrawer = withStyles({
    paper: {
        border: `1px solid ${Colors.Background}`,
        borderRadius: '10px 10px 0 0',
        height: '200px',
        overflow: 'hidden',
    },
})(Drawer)

const ReactionTitle = styled.div`
    font-size: ${FontSize.Body};
    line-height: 18px;
`

const CommentMenuDrawer = withStyles({
    paper: {
        border: `1px solid ${Colors.Background}`,
        borderRadius: '10px 10px 0 0',
        height: '125px',
    },
})(Drawer)

const StyledButton = styled(UnstyledLinkStyledButton)`
    padding: 20px 20px 20px 15px;
    line-height: 24px;
`

const CommentMenuPopover = withStyles({
    paper: {
        width: '245px',
        border: `1px solid ${Colors.Background}`,
        borderRadius: '10px',
    },
})(Popover)

const RowReverseFlexContainer = styled(FlexContainer)`
    flex-direction: row-reverse;
`

const UserNameAndRoomDisplayArea = styled(LeftRowFlexContainer)`
    -webkit-flex-wrap: wrap;
    line-height: 1.3;
`

const RoomNumberText = styled.span`
    white-space: normal;
    padding: 0 16px 0 0;
    font-size: 12px;
    color: ${Colors.AccentDark};
    width: 100%;
`

const CommentReactedUserIconBox = styled(Box)`
    padding-top: 2px;
    text-align: center;
    margin-right: 12px;
`
